import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

/**
 * Why the editor refused a UI command, as its ack tells Stitchy (MCP contract, 6 Oct: `sheets_ui`; NAV_FAILED's
 * `reason`). Never shown to a person, so not sheets.strings.ts's (NFR-8 covers what people read); kept here, in one
 * place, as editor-session.ts keeps ALG-NAV's acknowledgements. Where a reason names something on screen, it names
 * it in the editor's own words.
 */
const S = SHEETS_STRINGS;

export const UI_REASONS = {
  loading: 'the editor is still loading',
  notRun: 'nothing changed: the command did not run',
  pdfBusy: 'a PDF is being made',
  importBusy: 'a file is being imported',
  toolbarHidden: 'the toolbar is not showing',
  noOverflow: `every control fits in the toolbar: there is no "${S['More tools']}"`,
  downloadHidden: `${S['Download and share']} is not showing`,
  noItemMenu: 'the item menu shows only for a workbook item',
  fontOpensNothing: 'the font select has nothing to open: choose with select_option',
  noSurface: (surface: string) => `there is no ${surface}`,
  noItemToClose: 'this editor has no workbook item to close',
  noItemTabs: 'this editor has no workbook item: it has no tabs',
  noItemToOpen: 'this editor has no workbook item to open',
  noCompanion: (mode: 'modal' | 'dynamic_panel') => `${mode === 'modal' ? 'Open in Modal' : 'Open in Dynamic Panel'} isn't available on this page`,
  alreadyInPanel: 'this workbook is the one open in the Dynamic Panel',
  sheetById: 'name a sheet of another workbook by its id',
  conflictOpen: `the "${S['This workbook changed in another tab']}" dialog`,
  // The inspectors' fields.
  noField: (surface: 'validation' | 'cf' | 'split' | 'pdf', field: string) =>
    `the ${{ validation: 'validation panel', cf: 'conditional formatting panel', split: 'split panel', pdf: 'PDF dialog' }[surface]} has no field ${field}`,
  aString: 'a string',
  aList: 'a list of { value, colour? }',
  aWholeNumber: 'a whole number',
  oneCharacter: (field: string) => `${field} takes one character`,
  takesDate: (field: string) => `${field} takes a date as YYYY-MM-DD`,
  takesNumber: (field: string) => `${field} takes a number`,
  dateHasNo: (operator: string) => `a date rule has no ${operator}`,
  noOperator: (kind: string) => `a ${kind} condition takes no operator`,
  noRule: (id: string, listed: readonly string[]) => `the sheet has no rule ${id}; the panel lists ${listed.join(', ') || 'none'}`,
  newRuleNothingToRemove: 'there is no rule to remove: the panel is making a new one',
  chooseRuleFirst: 'there is no rule to remove: choose one with the field "rule" first',
  splitDisabled: `${S.Split} is disabled: "${S.Custom}" needs a separator of one character`,
} as const;
